import { useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowRight, Check, Clock } from 'lucide-react'
import { useTranslations } from 'use-intl'

import { cn } from '@/lib/utils'
import { trackAppCardClick } from '@/lib/analytics/events'
import { Button } from '@/components/ui/button'
import { BookDemoButton } from '@/components/cta-buttons'
import { HighlightText } from '@/components/ui/highlight-text'
import { CATEGORY_ICONS, ProductCategory } from '@/components/product-category'
import { Reveal, RevealGroup, RevealItem } from '@/components/ui/reveal'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { SOLUTIONS, SOLUTION_KEYS, type SolutionKey } from '@/lib/solutions'
import { SOLUTIONS_ANCHOR } from '@/features/landing-page/components/hero-section'

// Same gentle fade + slight glide as the compare bento swap.
const contentVariants = {
  enter: { opacity: 0, x: 6 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -6 },
}

const springPill = { type: 'spring', stiffness: 380, damping: 32 } as const

/** Category tab whose highlight slides between tabs via a shared `layoutId`,
    same as the compare tabs. Switches which product block shows. */
function CategoryTab({
  category,
  active,
}: {
  category: SolutionKey
  active: boolean
}) {
  const t = useTranslations('nav.solutionsItems')
  return (
    <TabsTrigger
      value={category}
      className="relative rounded-lg px-6 py-2.5 data-[state=inactive]:opacity-60 data-[state=active]:bg-transparent data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
    >
      {active ? (
        <motion.span
          layoutId="apps-tab-pill"
          className="absolute inset-0 rounded-lg border border-[#0211D9] bg-linear-to-r/srgb from-[#0211D9] to-[#2736F8]"
          transition={springPill}
        />
      ) : null}
      <span className="relative z-10 flex w-full items-center justify-center gap-2">
        <img
          src={CATEGORY_ICONS[category]}
          alt=""
          className={cn('size-4', active && 'brightness-0 invert')}
          aria-hidden
        />
        {t(category)}
      </span>
    </TabsTrigger>
  )
}

/** Fixed expert card pinned to the bottom of every app illustration. The
    frosted, translucent band keeps the copy legible over any image while
    letting the art breathe through. Only the quote changes per category. */
function ExpertBanner({ category }: { category: SolutionKey }) {
  const t = useTranslations('home.apps')
  const b = useTranslations('home.apps.expertBanner')
  return (
    <div className="absolute inset-x-0 bottom-0 border-t border-app-highlight/60 bg-background/80 px-4 py-3 backdrop-blur-md sm:px-5">
      <div className="flex items-start gap-3">
        <img
          src="/tom-basin.webp"
          alt={b('name')}
          loading="lazy"
          decoding="async"
          className="size-11 shrink-0 rounded-lg border-2 border-muted object-cover"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-sm font-semibold text-accent-foreground">
              {b('name')}
            </span>
            <span className="text-xs text-secondary-foreground">
              {b('role')}
            </span>
          </div>
          <p className="mt-1 text-xs text-secondary-foreground italic sm:text-sm">
            {t(`${category}.quote`)}
          </p>
          <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1.5 text-xs font-medium text-primary">
            <span className="inline-flex items-center gap-2">
              <Check className="size-3.5 shrink-0" />
              {b('included')}
            </span>
            <span className="inline-flex items-center gap-2">
              <Clock className="size-3.5 shrink-0" />
              {b('response')}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

/** One product block: content on the left, image placeholder on the right. */
function PlatformBlock({
  category,
  locale,
}: {
  category: SolutionKey
  locale: string
}) {
  const t = useTranslations('home.apps')
  const features = t.raw(`${category}.features`) as string[]

  return (
    <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
      <div>
        <ProductCategory
          category={category}
          variant="highlight"
          size="sm"
          className="rounded-md"
        />

        <h3 className="mt-4 font-serif text-3xl font-semibold tracking-tight sm:text-4xl">
          <span className="block text-accent-foreground">
            {t(`${category}.titleLead`)}
          </span>
          <span className="block text-primary">
            {t(`${category}.titleHighlight`)}
          </span>
        </h3>

        <p className="mt-4 max-w-lg text-base text-secondary-foreground">
          {t(`${category}.description`)}
        </p>

        <ul className="mt-6 space-y-3">
          {features.map((feature) => (
            <li key={feature} className="flex items-center gap-3">
              <span className="bg-app-highlight flex size-6 shrink-0 items-center justify-center rounded-md">
                <Check className="size-3.5 text-primary" />
              </span>
              <span className="text-sm text-accent-foreground">{feature}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <Button asChild>
            <Link to={SOLUTIONS[category].to as never} params={{ locale } as never}>
              {t(`${category}.discover`)}
              <ArrowRight />
            </Link>
          </Button>
          <BookDemoButton source="apps" variant="outline">{t('startCta')}</BookDemoButton>
        </div>
      </div>

      <div className="relative aspect-[4/3.3] overflow-hidden rounded-3xl border border-border bg-app-highlight-faint">
        <img
          src={SOLUTIONS[category].illustration}
          alt=""
          aria-hidden
          loading="lazy"
          decoding="async"
          className="pointer-events-none size-full object-cover object-top"
        />
        <ExpertBanner category={category} />
      </div>
    </div>
  )
}

export function AppsSection() {
  const t = useTranslations('home.apps')
  const { locale } = useParams({ from: '/$locale' })
  const [category, setCategory] = useState<SolutionKey>(SOLUTION_KEYS[0])

  return (
    // Target of the hero's "Découvrir les solutions" button; the margin keeps
    // the heading clear of the sticky header.
    <section
      id={SOLUTIONS_ANCHOR}
      className="scroll-mt-[calc(var(--header-h)+2rem)] pb-16 lg:pb-24"
    >
      <RevealGroup className="mx-auto max-w-7xl px-6 lg:px-8">
        <RevealItem className="mx-auto max-w-5xl text-center">
          <h2 className="font-serif text-4xl font-semibold tracking-tight sm:text-5xl">
            <HighlightText>{t('titleHighlight')}.</HighlightText>{' '}
            {t('titleRest')}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl font-mono text-sm text-secondary-foreground">
            {t('subtitle')}
          </p>
        </RevealItem>

        <RevealItem className="mt-8">
          <Tabs
            value={category}
            onValueChange={(v) => {
              setCategory(v as SolutionKey)
              trackAppCardClick({ app: v })
            }}
            className="items-center"
          >
            {/* Full-width two-up on mobile, centered rounded box on desktop. */}
            <TabsList className="grid h-auto w-full grid-cols-2 rounded-lg border border-border bg-transparent p-0 lg:inline-flex lg:w-auto">
              {SOLUTION_KEYS.map((key) => (
                <CategoryTab
                  key={key}
                  category={key}
                  active={category === key}
                />
              ))}
            </TabsList>
            {/* The product block (the real panel) is rendered below, outside
                Tabs. These stubs give each trigger's aria-controls a valid
                target so the tablist stays well-formed. */}
            {SOLUTION_KEYS.map((key) => (
              <TabsContent key={key} value={key} />
            ))}
          </Tabs>
        </RevealItem>
      </RevealGroup>

      {/* Content swaps with the same fade/glide as the compare bento. No Reveal
          wrapper here: the AnimatePresence owns the entrance, so it won't fight
          a second one. initial={false} keeps the first block from animating on
          mount. */}
      <Reveal className="mx-auto mt-12 max-w-7xl px-6 lg:mt-16 lg:px-8">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={category}
            variants={contentVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.22, ease: 'easeOut' }}
          >
            <PlatformBlock category={category} locale={locale} />
          </motion.div>
        </AnimatePresence>
      </Reveal>
    </section>
  )
}
